import { Client, ResponseType } from "@microsoft/microsoft-graph-client";
import ErrorOr from "shared/ErrorOr";
import OnBehalfOfAuthenticationProvider from "./authentication/OnBehalfOfAuthenticationProvider";
import { AadUserDetailsModel } from "../models";
import { AadUserPresence } from "../enums/AadUserPresence";

class MsGraph {
    private graph: Client;

    constructor() {
        this.graph = Client.initWithMiddleware({
            authProvider: OnBehalfOfAuthenticationProvider,
        });
    }

    async tryGetAadUserDetails(userUpn: string): Promise<ErrorOr<AadUserDetailsModel>> {
        try {
            const idResult = await new Promise<ErrorOr<string>>(async (resolve, _) => {
                try {
                    const result = await this.graph.api(`users/${userUpn}`).select("id").get();
                    resolve(ErrorOr.Success<string>(result.id));
                } catch {
                    resolve(ErrorOr.Failed<string>(new Error("Failed to get user ID.")));
                }
            });

            if (idResult.isFailure) {
                return ErrorOr.Failed<AadUserDetailsModel>(new Error("Failed to get AAD user details."));
            }

            const id = idResult.data!;

            const userPhotoPromise = new Promise<ErrorOr<string>>(async (resolve, _) => {
                try {
                    const result = await this.graph
                        .api(`users/${id}/photos/64x64/$value`)
                        .responseType(ResponseType.BLOB)
                        .get();
                    const base64DataUri = window.URL.createObjectURL(result);
                    resolve(ErrorOr.Success<string>(base64DataUri));
                } catch {
                    resolve(ErrorOr.Failed<string>(new Error("Failed to get user photo.")));
                }
            });

            const userPresencePromise = new Promise<ErrorOr<AadUserPresence>>(async (resolve, _) => {
                try {
                    const result = await this.graph.api(`users/${id}/presence`).get();
                    const availability = result.availability!;

                    let presence = AadUserPresence.offline;
                    switch (availability) {
                        case "Available":
                        case "AvailableIdle":
                            presence = AadUserPresence.online;
                            break;

                        case "Away":
                        case "BeRightBack":
                            presence = AadUserPresence.away;
                            break;

                        case "Busy":
                        case "BusyIdle":
                            presence = AadUserPresence.busy;
                            break;

                        case "DoNotDisturb":
                            presence = AadUserPresence.doNotDisturb;
                            break;
                    }

                    resolve(ErrorOr.Success<AadUserPresence>(presence));
                } catch {
                    resolve(ErrorOr.Failed<AadUserPresence>(new Error("Failed to get user presence.")));
                }
            });

            const [photoResult, presenceResult] = await Promise.all([userPhotoPromise, userPresencePromise]);

            let photo = "";
            if (photoResult.isSuccess) {
                photo = photoResult.data as string;
            }

            let presence = AadUserPresence.offline;
            if (presenceResult.isSuccess) {
                presence = presenceResult.data as AadUserPresence;
            }

            return ErrorOr.Success<AadUserDetailsModel>({
                base64Photo: photo,
                presence: presence,
            });
        } catch (error) {
            return ErrorOr.Failed<AadUserDetailsModel>(new Error("Failed to get AAD user details."));
        }
    }
}

export default new MsGraph();
